import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { accountRequest, type AccountSummary } from '../../utils/accountApi';
import { formatDate, getPlanDetails } from '../../utils/plans';
import LegalLinks from '../Legal/LegalLinks';

const MAX_ATTEMPTS = 6;
const RETRY_MS = 1500;

type Status = 'confirming' | 'active' | 'pending' | 'error' | 'signed_out';

const BillingSuccess: React.FC = () => {
  const { user, setAccount } = useAuth();
  const [status, setStatus] = useState<Status>(user ? 'confirming' : 'signed_out');
  const [account, setLocalAccount] = useState<AccountSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const token = user?.accountType === 'account' ? user.token : null;

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!token || !sessionId) {
      setStatus(token ? 'error' : 'signed_out');
      if (token) setError('Missing checkout reference.');
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const confirm = async (attempt: number) => {
      try {
        const result = await accountRequest<{ account: AccountSummary }>(
          '/billing/confirm',
          { method: 'POST', token, body: { session_id: sessionId } }
        );
        if (cancelled) return;
        if (result.account.plan !== 'none') {
          setAccount(result.account);
          setLocalAccount(result.account);
          setStatus('active');
        } else if (attempt + 1 < MAX_ATTEMPTS) {
          timer = setTimeout(() => confirm(attempt + 1), RETRY_MS);
        } else {
          setStatus('pending');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not confirm your payment.');
        setStatus('error');
      }
    };
    confirm(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, setAccount]);

  let content: React.ReactNode;
  if (status === 'active' && account && account.plan !== 'none') {
    const plan = getPlanDetails(account.plan);
    content = (
      <>
        <div className="text-5xl">🎉</div>
        <h1 className="mt-4 text-3xl font-bold text-slate-900">
          Welcome to {plan.name}
        </h1>
        <p className="mt-2 text-slate-600">
          Your subscription is active
          {account.current_period_end
            ? ` and renews on ${formatDate(account.current_period_end)}`
            : ''}
          . A receipt is on its way to {account.email}.
        </p>
        <ul className="mx-auto mt-6 max-w-sm list-none space-y-2 p-0 text-left text-sm text-slate-700">
          {plan.features.slice(0, 5).map((feature) => (
            <li key={feature}>✓ {feature}</li>
          ))}
        </ul>
        <a
          href="/"
          className="mt-8 inline-block rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3 font-semibold text-white no-underline shadow-lg"
        >
          Start practising
        </a>
      </>
    );
  } else if (status === 'confirming') {
    content = (
      <>
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
        <p className="mt-4 text-slate-600">Confirming your payment…</p>
      </>
    );
  } else if (status === 'pending') {
    content = (
      <>
        <h1 className="text-2xl font-bold text-slate-900">Payment received</h1>
        <p className="mt-2 text-slate-600">
          We&apos;re still activating your plan. This usually takes a few
          seconds. Refresh this page, or open the app in a minute.
        </p>
        <a href="/" className="mt-6 inline-block text-indigo-600">
          Go to the app
        </a>
      </>
    );
  } else if (status === 'signed_out') {
    content = (
      <>
        <h1 className="text-2xl font-bold text-slate-900">Almost there</h1>
        <p className="mt-2 text-slate-600">
          Sign in with the email you subscribed with to start practising.
        </p>
        <a href="/" className="mt-6 inline-block text-indigo-600">
          Sign in
        </a>
      </>
    );
  } else {
    content = (
      <>
        <h1 className="text-2xl font-bold text-slate-900">
          We couldn&apos;t confirm your payment
        </h1>
        <p role="alert" className="mt-2 text-slate-600">
          {error} If you were charged, your plan will activate shortly. Check
          your Plan &amp; billing page.
        </p>
        <a href="/account" className="mt-6 inline-block text-indigo-600">
          Plan &amp; billing
        </a>
      </>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 p-5">
      <div className="w-full max-w-lg rounded-3xl bg-white p-10 text-center shadow-2xl ring-1 ring-slate-200">
        {content}
      </div>
      <LegalLinks className="mt-6 text-slate-500" />
    </div>
  );
};

export default BillingSuccess;
